// Lightweight email sanity checks used on login, signup and the account page:
//  1. Format check (regex).
//  2. Typo suggestion for common domains ("gamil.com" → "gmail.com").
//  3. Domain MX lookup via free, CORS-enabled DNS-over-HTTPS (no backend, no cost).

const POPULAR_DOMAINS = [
  'gmail.com', 'googlemail.com',
  'hotmail.com', 'hotmail.es', 'hotmail.com.mx',
  'outlook.com', 'outlook.es', 'live.com', 'live.com.mx', 'msn.com',
  'yahoo.com', 'yahoo.es', 'yahoo.com.mx',
  'icloud.com', 'me.com', 'mac.com',
  'proton.me', 'protonmail.com',
  'aol.com', 'gmx.com', 'zoho.com',
]

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function isValidEmailFormat(email: string): boolean {
  return EMAIL_RE.test(email.trim())
}

/** Damerau-lite Levenshtein distance (rolling 1D DP). */
function editDistance(a: string, b: string): number {
  const dp: number[] = Array.from({ length: b.length + 1 }, (_, i) => i)
  for (let i = 1; i <= a.length; i++) {
    let prev = dp[0]!
    dp[0] = i
    for (let j = 1; j <= b.length; j++) {
      const tmp = dp[j]!
      dp[j] = a[i - 1] === b[j - 1] ? prev : 1 + Math.min(prev, dp[j]!, dp[j - 1]!)
      prev = tmp
    }
  }
  return dp[b.length]!
}

/**
 * Suggest a corrected email when the domain looks like a typo of a popular one.
 * Returns the full corrected email, or null when nothing looks off.
 */
export function suggestEmail(email: string): string | null {
  const value = email.trim().toLowerCase()
  const at = value.lastIndexOf('@')
  if (at < 1) return null
  const local = value.slice(0, at)
  const domain = value.slice(at + 1)
  if (!domain || domain.includes(' ') || POPULAR_DOMAINS.includes(domain)) return null

  let best: string | null = null
  let bestDist = Infinity
  for (const d of POPULAR_DOMAINS) {
    const dist = editDistance(domain, d)
    if (dist < bestDist) { bestDist = dist; best = d }
  }
  // Only nudge on a close typo (1–2 edits) of a similar-length domain.
  if (best && bestDist > 0 && bestDist <= 2 && Math.abs(best.length - domain.length) <= 2) {
    return `${local}@${best}`
  }
  return null
}

/**
 * Whether the domain can receive email (has MX, or exists at all).
 * Uses Google's public DNS-over-HTTPS JSON API (CORS-enabled, free).
 * Returns:  true = deliverable · false = no such domain / no mail servers · null = couldn't determine.
 */
export async function domainHasMx(domain: string): Promise<boolean | null> {
  const d = domain.trim().toLowerCase()
  if (!d || !d.includes('.')) return false
  try {
    const res = await fetch(`https://dns.google/resolve?name=${encodeURIComponent(d)}&type=MX`, {
      headers: { Accept: 'application/dns-json' },
    })
    if (!res.ok) return null
    const data = (await res.json()) as { Status?: number; Answer?: { type: number }[] }
    if (data.Status === 3) return false // NXDOMAIN — domain doesn't exist
    if (data.Status !== 0) return null // SERVFAIL / other — unknown, don't block
    return Array.isArray(data.Answer) && data.Answer.some((a) => a.type === 15) // 15 = MX
  } catch {
    return null // network error — unknown
  }
}
